/* TaxMapperTab — Chart-of-accounts → tax-form-line mapping.
 *
 * Originally lived as an inline component inside CFOBuck.js. Extracted as part
 * of the Feb-2026 IA refactor that moved Tax Mapper out of CFO Buck into the
 * Accounting Hub (it's a setup/configuration tool, not an advisory tool).
 */
import { useState, useEffect } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, Map, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { API_URL } from './constants';
import TaxMapperSuggestionsModal from './TaxMapperSuggestionsModal';

export function TaxMapperTab({ workspaceId }) {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formLines, setFormLines] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [formsRes, acctRes, mapRes] = await Promise.all([
          apiFetch(`${API_URL}/api/tax-mapper/forms`),
          apiFetch(`${API_URL}/api/workspaces/${workspaceId}/accounts`),
          apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper`),
        ]);

        if (formsRes.ok) setForms(await formsRes.json());
        if (acctRes.ok) {
          const allAccts = await acctRes.json();
          setAccounts(allAccts.filter(a => a.is_active !== false));
        }
        if (mapRes.ok) {
          const mapData = await mapRes.json();
          if (mapData.form_type) setSelectedForm(mapData.form_type);
          const mapObj = {};
          for (const m of (mapData.mappings || [])) {
            mapObj[m.account_id] = { form_type: m.form_type, line: m.line, line_description: m.line_description };
          }
          setMappings(mapObj);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedForm) { setFormLines(null); return; }
    const loadLines = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/tax-mapper/forms/${selectedForm}/lines`);
        if (res.ok) setFormLines(await res.json());
      } catch { /* ignore */ }
    };
    loadLines();
  }, [selectedForm]);

  const handleMapping = (accountId, line) => {
    setSaved(false);
    if (!line) {
      setMappings(prev => { const next = { ...prev }; delete next[accountId]; return next; });
    } else {
      const lineDesc = formLines?.sections?.flatMap(s => s.lines).find(l => l.line === line)?.description || '';
      setMappings(prev => ({ ...prev, [accountId]: { form_type: selectedForm, line, line_description: lineDesc } }));
    }
  };

  const handleSave = async () => {
    if (!selectedForm) return;
    setSaving(true);
    try {
      const entries = Object.entries(mappings)
        .filter(([, v]) => v.form_type === selectedForm)
        .map(([account_id, v]) => ({ account_id, form_type: selectedForm, line: v.line }));

      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_type: selectedForm, mappings: entries }),
      });
      if (res.ok) setSaved(true);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!selectedForm || !window.confirm('Clear all mappings for this form?')) return;
    try {
      await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper?form_type=${selectedForm}`, { method: 'DELETE' });
      setMappings(prev => {
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v.form_type !== selectedForm) next[k] = v;
        }
        return next;
      });
      setSaved(false);
    } catch { /* ignore */ }
  };

  const lineOptions = formLines?.sections?.flatMap(s =>
    s.lines.map(l => ({ ...l, section: s.name }))
  ) || [];

  const filtered = accounts.filter(a => {
    if (filterType !== 'all' && a.type !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.code || '').toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const mappedCount = Object.values(mappings).filter(m => m.form_type === selectedForm).length;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="tax-mapper-tab">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-mapper-tab">
      <div>
        <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
          Map your chart of accounts to specific tax form lines. This is optional but helps with tax preparation by pre-assigning where each account flows on your return.
        </p>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2.5 block">Select Tax Form</span>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {forms.map(f => (
            <button key={f.id} onClick={() => { setSelectedForm(f.id); setSaved(false); }}
              className={cn("flex flex-col items-center gap-1.5 p-3.5 rounded-xl text-center transition-all border",
                selectedForm === f.id
                  ? "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700 shadow-sm"
                  : "bg-card border-border hover:border-primary/30"
              )} data-testid={`form-type-${f.id}`}>
              <span className={cn("text-sm font-bold", selectedForm === f.id ? "text-emerald-700 dark:text-emerald-400" : "text-foreground")}>
                {f.name}
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">{f.total_lines} lines</span>
            </button>
          ))}
        </div>
      </div>

      {selectedForm && formLines && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <Input placeholder="Search accounts..." value={search} onChange={e => setSearch(e.target.value)}
                className="max-w-xs text-sm" data-testid="mapper-search" />
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-border bg-card text-foreground" data-testid="mapper-filter">
                <option value="all">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
                <option value="asset">Asset</option>
                <option value="liability">Liability</option>
                <option value="equity">Equity</option>
              </select>
              <Badge variant="outline" className="text-[10px] flex-shrink-0">
                {mappedCount} of {accounts.length} mapped
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {saved && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSuggestions(true)}
                disabled={!selectedForm || accounts.length === 0}
                className="gap-1 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                data-testid="tax-mapper-ai-suggest-btn"
              >
                <Sparkles className="w-3 h-3" /> Auto-suggest
              </Button>
              <Button size="sm" variant="outline" onClick={handleClear} data-testid="clear-mappings-btn">
                <XCircle className="w-3 h-3 mr-1" /> Clear All
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="save-mappings-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Save Mappings
              </Button>
            </div>
          </div>

          <Card className="border-border">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="mapper-table">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground w-24">Code</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Account Name</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground w-24">Type</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground w-96">{formLines.name} Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(acct => {
                      const mapping = mappings[acct.account_id];
                      const currentLine = (mapping?.form_type === selectedForm) ? mapping.line : '';
                      return (
                        <tr key={acct.account_id} className={cn(
                          "border-b border-border transition-colors",
                          currentLine ? "bg-emerald-50/30 dark:bg-emerald-900/5" : ""
                        )} data-testid={`mapper-row-${acct.code}`}>
                          <td className="p-3 font-mono text-xs text-muted-foreground">{acct.code}</td>
                          <td className="p-3 text-foreground font-medium">{acct.name}</td>
                          <td className="p-3">
                            <Badge variant="outline" className={cn("text-[10px] capitalize border-0",
                              acct.type === 'income' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                              acct.type === 'expense' ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" :
                              acct.type === 'asset' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                              acct.type === 'liability' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                              "bg-muted text-muted-foreground"
                            )}>
                              {acct.type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <select
                              value={currentLine}
                              onChange={e => handleMapping(acct.account_id, e.target.value)}
                              className={cn(
                                "w-full text-xs px-2 py-1.5 rounded-lg border bg-card focus:ring-1 focus:ring-emerald-500 focus:outline-none",
                                currentLine
                                  ? "border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                                  : "border-border text-muted-foreground"
                              )}
                              data-testid={`mapper-select-${acct.code}`}
                            >
                              <option value="">-- Not Mapped --</option>
                              {lineOptions.map(l => (
                                <option key={l.line} value={l.line}>
                                  Line {l.line}: {l.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-sm text-muted-foreground">
                          No accounts match your filter
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!selectedForm && (
        <Card className="border-dashed border-2 border-border">
          <CardContent className="p-12 text-center">
            <Map className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-base font-semibold text-muted-foreground">Select a tax form above to begin mapping</p>
            <p className="text-sm text-muted-foreground mt-1">Choose the form that matches your business entity type</p>
          </CardContent>
        </Card>
      )}

      {showSuggestions && selectedForm && formLines && (
        <TaxMapperSuggestionsModal
          workspaceId={workspaceId}
          formType={selectedForm}
          formLines={formLines}
          onClose={() => setShowSuggestions(false)}
          onApplied={async () => {
            // Refetch mappings so the table reflects what AI just persisted
            try {
              const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/tax-mapper`);
              if (r.ok) {
                const mapData = await r.json();
                const mapObj = {};
                for (const m of (mapData.mappings || [])) {
                  mapObj[m.account_id] = { form_type: m.form_type, line: m.line, line_description: m.line_description };
                }
                setMappings(mapObj);
              }
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}
